import React from 'react';
import { Box, Typography } from '@mui/material';
import CountUp from '../fx/CountUp';
import { orchard } from '../../theme';

/* Compact number tile. `glass` is the translucent variant that sits on top
   of the dark hero; the default one is a bordered card for light/dark pages.
   Numeric values count up, strings are shown as they are. The tile carries
   the attribute named by `revealAttr` so a parent's stagger (hero intro or
   useStaggerReveal) picks it up. */
export default function StatTile({
  icon,
  label,
  value,
  suffix = '',
  subtitle,
  color = orchard.cherry,
  glass = false,
  delay = 0,
  revealAttr = 'data-hero-item',
}) {
  const numeric = typeof value === 'number' && Number.isFinite(value);
  return (
    <Box
      {...{ [revealAttr]: true }}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        minWidth: 0,
        height: '100%',
        p: { xs: 1.25, sm: 1.75 },
        pl: { xs: glass ? 1.25 : 1.75, sm: glass ? 1.75 : 2.25 },
        borderRadius: '16px',
        border: '1px solid',
        borderColor: glass ? 'rgba(255,255,255,0.16)' : 'divider',
        bgcolor: glass ? 'rgba(22,16,32,0.5)' : 'background.paper',
        color: glass ? '#fff' : 'text.primary',
        overflow: 'hidden',
        '&::after': glass
          ? undefined
          : {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 12,
              bottom: 12,
              width: 3,
              borderRadius: 3,
              bgcolor: color,
            },
      }}
    >
      {icon && (
        <Box
          sx={{
            width: { xs: 34, sm: 40 },
            height: { xs: 34, sm: 40 },
            borderRadius: '12px',
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            color: glass ? '#fff' : color,
            bgcolor: glass ? `${color}66` : `${color}1c`,
            '& svg': { fontSize: { xs: 18, sm: 22 } },
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="div"
          sx={{
            fontFamily: orchard.fontDisplay,
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            fontSize: { xs: '1.2rem', sm: '1.5rem' },
          }}
        >
          {numeric ? (
            <CountUp value={value} suffix={suffix} delay={delay} />
          ) : (
            `${value}${suffix}`
          )}
        </Typography>
        <Typography
          component="div"
          sx={{
            fontSize: { xs: '0.7rem', sm: '0.78rem' },
            fontWeight: 600,
            lineHeight: 1.25,
            // two lines on phones instead of «Оценено з…»
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mt: 0.25,
            opacity: glass ? 0.88 : 1,
            color: glass ? 'inherit' : 'text.secondary',
          }}
        >
          {label}
        </Typography>
        {subtitle && (
          <Typography
            component="div"
            noWrap
            sx={{
              fontSize: '0.66rem',
              lineHeight: 1.2,
              color: glass ? 'rgba(255,255,255,0.6)' : 'text.disabled',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
